import { useState, useCallback } from 'react';

const moveEntryByDirection = (sourceObj, keyToMove, direction) => {
  const entries = Object.entries(sourceObj);
  const fromIndex = entries.findIndex(([key]) => key === keyToMove);
  if (fromIndex < 0) return sourceObj;

  const offset = direction === 'up' ? -1 : 1;
  const toIndex = fromIndex + offset;
  if (toIndex < 0 || toIndex >= entries.length) return sourceObj;

  const reordered = [...entries];
  [reordered[fromIndex], reordered[toIndex]] = [reordered[toIndex], reordered[fromIndex]];
  return Object.fromEntries(reordered);
};

export function useStatusEditor(saveStateRef, statusOptions, setStatusOptions, albumStatusOptions, setAlbumStatusOptions, tasks, setTasks, albums, setAlbums, currentUser, taskStatusConfig, setTaskStatusConfig) {
  const [isEditStatusModalOpen, setIsEditStatusModalOpen] = useState(false);
  const [editingStatuses, setEditingStatuses] = useState({});
  const [newStatusInput, setNewStatusInput] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('bg-blue-500 text-white');

  const [isEditAlbumStatusModalOpen, setIsEditAlbumStatusModalOpen] = useState(false);
  const [editingAlbumStatuses, setEditingAlbumStatuses] = useState({});
  const [newAlbumStatusInput, setNewAlbumStatusInput] = useState('');
  const [newAlbumStatusColor, setNewAlbumStatusColor] = useState('bg-blue-500 text-white');

  const handleAddStatusOption = useCallback((newStatusName) => {
    if (!statusOptions[newStatusName]) {
      const colors = ['bg-pink-500 text-white', 'bg-indigo-500 text-white', 'bg-teal-500 text-white', 'bg-lime-500 text-slate-900', 'bg-rose-500 text-white', 'bg-emerald-500 text-white', 'bg-sky-500 text-slate-900', 'bg-fuchsia-500 text-white'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];
      const newOpts = { ...statusOptions, [newStatusName]: randomColor };
      setStatusOptions(newOpts);
      saveStateRef.current({ statusOptions: newOpts });
    }
  }, [statusOptions, setStatusOptions, saveStateRef]);

  const handleOpenEditStatusModal = useCallback(() => {
    if (currentUser?.role !== 'admin') return;
    const initialEdits = {};
    Object.keys(statusOptions).forEach(key => {
      if (key !== 'VAZIO') {
        const cfg = taskStatusConfig?.[key] || {};
        initialEdits[key] = { name: key, color: statusOptions[key], isNew: false, slaDays: cfg.slaDays ?? 5, controlaPrazo: cfg.controlaPrazo ?? true };
      }
    });
    setEditingStatuses(initialEdits);
    setNewStatusInput('');
    setNewStatusColor('bg-blue-500 text-white');
    setIsEditStatusModalOpen(true);
  }, [statusOptions, currentUser, taskStatusConfig]);

  const handleUpdateEditingStatus = useCallback((originalKey, field, value) => {
    setEditingStatuses(prev => ({
      ...prev,
      [originalKey]: { ...prev[originalKey], [field]: value }
    }));
  }, []);

  const handleMoveEditingStatus = useCallback((keyToMove, direction) => {
    setEditingStatuses(prev => moveEntryByDirection(prev, keyToMove, direction));
  }, []);

  const handleDeleteEditingStatus = useCallback((keyToDelete) => {
    setEditingStatuses(prev => {
      const newEdits = { ...prev };
      delete newEdits[keyToDelete];
      return newEdits;
    });
  }, []);

  const handleAddNewStatusInModal = useCallback(() => {
    if (!newStatusInput.trim()) return;
    const key = newStatusInput.trim().toUpperCase();
    if (editingStatuses[key] || key === 'VAZIO') {
      alert('Já existe um status com este nome');
      return;
    }
    setEditingStatuses(prev => ({
      ...prev,
      [key]: { name: key, color: newStatusColor, isNew: true, slaDays: 5, controlaPrazo: true }
    }));
    setNewStatusInput('');
  }, [newStatusInput, newStatusColor, editingStatuses]);

  const handleSaveAllStatusEdits = useCallback(() => {
    if (currentUser?.role !== 'admin') return;

    const newOpts = { VAZIO: statusOptions.VAZIO || 'bg-slate-700 text-white' };
    const newConfig = { VAZIO: taskStatusConfig?.VAZIO || { slaDays: 0, controlaPrazo: false } };
    const deletedKeys = [];
    const renamedTasks = {};

    Object.keys(statusOptions).forEach(key => {
      if (key !== 'VAZIO' && !editingStatuses[key]) {
        deletedKeys.push(key);
        delete newOpts[key];
      }
    });

    Object.entries(editingStatuses).forEach(([originalKey, data]) => {
      const finalName = data.isNew ? data.name : (originalKey !== data.name ? data.name : originalKey);
      if (data.isNew) {
        newOpts[data.name] = data.color;
      } else if (originalKey !== data.name) {
        newOpts[data.name] = data.color;
        renamedTasks[originalKey] = data.name;
      } else {
        newOpts[originalKey] = data.color;
      }
      newConfig[finalName] = { slaDays: Number(data.slaDays) || 0, controlaPrazo: Boolean(data.controlaPrazo) };
    });

    setStatusOptions(newOpts);
    setTaskStatusConfig(newConfig);
    saveStateRef.current({ statusOptions: newOpts, taskStatusConfig: newConfig });

    const updatedTasks = tasks.map(task => {
      if (renamedTasks[task.status]) {
        return { ...task, status: renamedTasks[task.status] };
      }
      if (deletedKeys.includes(task.status)) {
        return { ...task, status: 'VAZIO' };
      }
      return task;
    });

    setTasks(updatedTasks);
    saveStateRef.current({ tasks: updatedTasks });

    setIsEditStatusModalOpen(false);
    setEditingStatuses({});
    setNewStatusInput('');
  }, [currentUser, statusOptions, editingStatuses, setStatusOptions, setTasks, tasks, saveStateRef, taskStatusConfig, setTaskStatusConfig]);

  // --- Album Status ---
  const handleOpenEditAlbumStatusModal = useCallback(() => {
    if (currentUser?.role !== 'admin') return;
    const initialEdits = {};
    Object.keys(albumStatusOptions).forEach(key => {
      initialEdits[key] = { name: key, color: albumStatusOptions[key], isNew: false };
    });
    setEditingAlbumStatuses(initialEdits);
    setNewAlbumStatusInput('');
    setNewAlbumStatusColor('bg-blue-500 text-white');
    setIsEditAlbumStatusModalOpen(true);
  }, [albumStatusOptions, currentUser]);

  const handleUpdateEditingAlbumStatus = useCallback((originalKey, field, value) => {
    setEditingAlbumStatuses(prev => ({
      ...prev,
      [originalKey]: { ...prev[originalKey], [field]: value }
    }));
  }, []);

  const handleMoveEditingAlbumStatus = useCallback((keyToMove, direction) => {
    setEditingAlbumStatuses(prev => moveEntryByDirection(prev, keyToMove, direction));
  }, []);

  const handleDeleteEditingAlbumStatus = useCallback((keyToDelete) => {
    setEditingAlbumStatuses(prev => {
      const newEdits = { ...prev };
      delete newEdits[keyToDelete];
      return newEdits;
    });
  }, []);

  const handleAddNewAlbumStatusInModal = useCallback(() => {
    if (!newAlbumStatusInput.trim()) return;
    const key = newAlbumStatusInput.trim().toUpperCase();
    if (editingAlbumStatuses[key]) {
      alert('Já existe um status com este nome');
      return;
    }
    setEditingAlbumStatuses(prev => ({
      ...prev,
      [key]: { name: key, color: newAlbumStatusColor, isNew: true }
    }));
    setNewAlbumStatusInput('');
  }, [newAlbumStatusInput, newAlbumStatusColor, editingAlbumStatuses]);

  const handleSaveAllAlbumStatusEdits = useCallback(() => {
    if (currentUser?.role !== 'admin') return;

    const newOpts = {};
    const deletedKeys = [];
    const renamedAlbums = {};

    Object.keys(albumStatusOptions).forEach(key => {
      if (!editingAlbumStatuses[key]) {
        deletedKeys.push(key);
      }
    });

    Object.entries(editingAlbumStatuses).forEach(([originalKey, data]) => {
      if (data.isNew) {
        newOpts[data.name] = data.color;
      } else if (originalKey !== data.name) {
        newOpts[data.name] = data.color;
        renamedAlbums[originalKey] = data.name;
      } else {
        newOpts[originalKey] = data.color;
      }
    });

    setAlbumStatusOptions(newOpts);
    saveStateRef.current({ albumStatusOptions: newOpts });

    const updatedAlbums = albums.map(album => {
      if (renamedAlbums[album.status]) {
        return { ...album, status: renamedAlbums[album.status] };
      }
      if (deletedKeys.includes(album.status)) {
        return { ...album, status: 'INICIAR PROJETO' };
      }
      return album;
    });

    setAlbums(updatedAlbums);
    saveStateRef.current({ albums: updatedAlbums });

    setIsEditAlbumStatusModalOpen(false);
    setEditingAlbumStatuses({});
    setNewAlbumStatusInput('');
  }, [currentUser, albumStatusOptions, editingAlbumStatuses, setAlbumStatusOptions, setAlbums, albums, saveStateRef]);

  return {
    isEditStatusModalOpen, setIsEditStatusModalOpen,
    editingStatuses, newStatusInput, setNewStatusInput,
    newStatusColor, setNewStatusColor,
    handleAddStatusOption,
    handleOpenEditStatusModal, handleUpdateEditingStatus,
    handleMoveEditingStatus,
    handleDeleteEditingStatus, handleAddNewStatusInModal,
    handleSaveAllStatusEdits,
    isEditAlbumStatusModalOpen, setIsEditAlbumStatusModalOpen,
    editingAlbumStatuses, newAlbumStatusInput, setNewAlbumStatusInput,
    newAlbumStatusColor, setNewAlbumStatusColor,
    handleOpenEditAlbumStatusModal, handleUpdateEditingAlbumStatus,
    handleMoveEditingAlbumStatus,
    handleDeleteEditingAlbumStatus, handleAddNewAlbumStatusInModal,
    handleSaveAllAlbumStatusEdits,
  };
}
